import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useDelayedBoolean } from '@/hooks/useDelayedBoolean';
import { useGetCreditBalancesQuery } from '@/services/endpoints/credits';
import {
  CampaignCreditChannel,
  CREDIT_TYPE_BY_CHANNEL,
  EMAIL_PACKAGE_TIERS,
  SMS_PACKAGE_TIERS,
  WHATSAPP_PACKAGE_CATEGORIES,
  CreditPackageTier,
  formatEuroNet,
  getWhatsappPackageTiers,
} from '../credits/config';
import { CampaignsCreditRequestModal, toCreditRequestPackage, CreditRequestPackage } from '../credits';
import { formatCredits } from '../credits/utils/formatCredits';
import { isCreditsFeatureUnavailable } from '../credits/utils/isCreditsFeatureUnavailable';
import { CampaignsCreditPackageCard } from './CampaignsCreditPackageCard';

const MINIMUM_LOADING_DURATION = 300;
const VISIBLE_TIER_COUNT = 4;

type CampaignsCreditPackagesSectionProps = {
  channel: CampaignCreditChannel;
};

export const CampaignsCreditPackagesSection: FC<CampaignsCreditPackagesSectionProps> = ({ channel }) => {
  const { t, i18n } = useTranslation('campaign');
  const workspace = useWorkspace();
  const creditType = CREDIT_TYPE_BY_CHANNEL[channel];

  const [whatsappCategory, setWhatsappCategory] = useState('marketing');
  const [showAllTiers, setShowAllTiers] = useState(false);
  const [request, setRequest] = useState<CreditRequestPackage | null>(null);

  const { data, isLoading, isFetching, isError, error, isUninitialized } = useGetCreditBalancesQuery(
    { workspaceId: workspace.id, type: creditType },
    { skip: !workspace.id }
  );

  const isLoadingDelayed = useDelayedBoolean(isLoading || isFetching, MINIMUM_LOADING_DURATION);

  const allTiers: CreditPackageTier[] = useMemo(() => {
    if (channel === 'whatsapp') {
      return getWhatsappPackageTiers(whatsappCategory);
    }
    if (channel === 'sms') {
      return SMS_PACKAGE_TIERS;
    }
    return EMAIL_PACKAGE_TIERS;
  }, [channel, whatsappCategory]);

  const visibleTiers = channel === 'whatsapp' || showAllTiers ? allTiers.slice(0, channel === 'whatsapp' ? 4 : allTiers.length) : allTiers.slice(0, VISIBLE_TIER_COUNT);
  const hasMore = channel !== 'whatsapp' && allTiers.length > VISIBLE_TIER_COUNT;

  if (isCreditsFeatureUnavailable(error)) {
    return null;
  }

  const balance = data?.find((item) => item.creditType === creditType) ?? data?.[0];
  const channelLabel = t(`tabs.${channel}` as 'tabs.whatsapp');
  const volumeUnit =
    channel === 'whatsapp' ? t('credits.packages.unitWhatsapp') : channel === 'sms' ? t('credits.packages.unitSms') : t('credits.packages.unitEmail');

  const openRequest = (tier: CreditPackageTier) => {
    setRequest(toCreditRequestPackage(tier, channelLabel));
  };

  const openCustomRequest = () => {
    setRequest({
      name: t('credits.packages.customTitle'),
      volume: 0,
      priceNet: null,
      channelLabel,
    });
  };

  const showLoading = isUninitialized || isLoading || isLoadingDelayed;

  return (
    <>
      <Loadable isLoading={showLoading}>
        <section className='space-y-6 rounded-3xl border border-secondary-border bg-surface p-5 shadow-sm md:p-6'>
          <div className='space-y-1'>
            <p className='text-xs font-semibold uppercase tracking-[0.16em] text-quaternary-500'>{t(`credits.packages.eyebrow.${channel}`)}</p>
            <h2 className='text-3xl font-semibold text-slate-900'>{t(`credits.packages.heading.${channel}`)}</h2>
            <p className='text-sm text-secondary-600'>{t(`credits.packages.subheading.${channel}`)}</p>
          </div>

          {balance && !isError ? (
            <div className='rounded-2xl bg-gradient-to-r from-blue-800 to-blue-600 p-5 text-white-100 md:p-6'>
              <div className='flex flex-col gap-5 md:flex-row md:items-end md:justify-between'>
                <div className='max-w-2xl space-y-2'>
                  <p className='text-xs font-semibold uppercase tracking-[0.16em] text-blue-100'>{t('credits.packages.balanceTitle')}</p>
                  <h3 className='text-2xl font-semibold'>{t(`credits.packages.balanceSubtitle.${channel}`)}</h3>
                  <p className='text-sm leading-6 text-blue-100'>{t(`credits.packages.balanceDescription.${channel}`)}</p>
                </div>
                <div className='w-full rounded-xl border border-blue-300/40 bg-blue-900/30 p-4 md:w-auto md:min-w-[220px]'>
                  <p className='text-[11px] uppercase tracking-[0.16em] text-blue-100'>{t('credits.packages.balanceLabel')}</p>
                  <p className='mt-2 text-3xl font-semibold'>{formatCredits(balance.available, i18n.language)}</p>
                  <p className='text-xs text-blue-100'>{t('credits.balance.availableLabel')}</p>
                </div>
              </div>
            </div>
          ) : null}

          {channel === 'whatsapp' ? (
            <div className='space-y-3'>
              <div className='inline-flex rounded-xl bg-secondary-background p-1'>
                {WHATSAPP_PACKAGE_CATEGORIES.map((category) => (
                  <button
                    key={category.id}
                    type='button'
                    onClick={() => setWhatsappCategory(category.id)}
                    className={`rounded-lg px-5 py-2.5 text-sm font-bold ${
                      whatsappCategory === category.id ? 'bg-white-100 text-slate-900 shadow-sm' : 'bg-transparent text-secondary-500'
                    }`}
                  >
                    {t(category.labelKey as 'credits.packages.categories.marketing')}
                  </button>
                ))}
              </div>
              <p className='text-sm text-secondary-600'>{t('credits.packages.whatsappPoolNote')}</p>
            </div>
          ) : null}

          <div className='space-y-4'>
            <div className='text-center'>
              <p className='text-xs font-semibold uppercase tracking-[0.16em] text-quaternary-500'>{t('credits.packages.packsEyebrow')}</p>
              <h3 className='text-xl font-semibold text-slate-900'>{t('credits.packages.packsHeading')}</h3>
            </div>

            <div className='grid gap-4 md:grid-cols-2 xl:grid-cols-4'>
              {visibleTiers.map((tier) => (
                <CampaignsCreditPackageCard
                  key={tier.id}
                  title={tier.name}
                  volume={tier.volume}
                  priceLabel={formatEuroNet(tier.priceNet, i18n.language)}
                  vatLabel={t('credits.packages.vatNote')}
                  ctaLabel={t('credits.packages.requestCta')}
                  volumeUnit={volumeUnit}
                  isPopular={tier.isPopular}
                  savingsBadge={tier.savingsBadge}
                  onRequest={() => openRequest(tier)}
                />
              ))}
            </div>

            {channel === 'whatsapp' ? (
              <button
                type='button'
                onClick={openCustomRequest}
                className='flex w-full flex-col items-start gap-1 rounded-[14px] border border-dashed border-secondary-border bg-white-100 px-6 py-5 text-left hover:border-brand-400'
              >
                <span className='text-lg font-extrabold text-slate-900'>{t('credits.packages.customTitle')}</span>
                <span className='text-sm text-secondary-600'>{t('credits.packages.customText')}</span>
                <span className='mt-2 text-sm font-bold text-brand-700'>{t('credits.packages.requestCta')}</span>
              </button>
            ) : null}

            {hasMore ? (
              <div className='flex justify-center'>
                <button
                  type='button'
                  onClick={() => setShowAllTiers((value) => !value)}
                  className='rounded-lg border border-secondary-border px-4 py-2 text-sm font-semibold text-slate-800 hover:bg-gray-50'
                >
                  {showAllTiers ? t('credits.packages.hideTiers') : t('credits.packages.showAllTiers')}
                </button>
              </div>
            ) : null}
          </div>
        </section>
      </Loadable>

      <CampaignsCreditRequestModal open={Boolean(request)} request={request} onClose={() => setRequest(null)} />
    </>
  );
};
